/**
 * Проверка розыгрыша в браузере: загрузка пакета (с сервера или из файла), независимый пересчёт
 * всех пунктов, выгрузка CSV для сравнения котировок с внешними источниками.
 */
import { useEffect, useState } from 'react';
import { actionsCsv, aggregateCsv, componentsCsv, type SignedPackage, type VerifyReport } from '@mt/verification';
import { saveFile, verify } from '../lib/verification';
import { Dialog } from './Dialog';

const ICON: Record<string, string> = { ok: '✓', fail: '✗', warn: '!', skip: '·' };

export function VerifyDialog({ initial, onClose }: { initial?: SignedPackage | null; onClose: () => void }) {
  const [signed, setSigned] = useState<SignedPackage | null>(initial ?? null);
  const [report, setReport] = useState<VerifyReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!signed) return;
    setBusy(true);
    setReport(null);
    verify(signed)
      .then(setReport)
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(false));
  }, [signed]);

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    try {
      setSigned(JSON.parse(await f.text()) as SignedPackage);
    } catch {
      setError('Файл не является JSON-пакетом проверки');
    }
  };

  const pkg = signed?.package;
  const stem = pkg ? `market-table-${pkg.game.gameId}` : 'market-table';
  return (
    <Dialog title="Проверка розыгрыша" onClose={onClose} wide>
      <div className="verify">
        <div className="verify-source">
          <label className="btn">
            Выбрать файл пакета…
            <input type="file" accept="application/json,.json" hidden onChange={(e) => void onFile(e.target.files?.[0])} />
          </label>
          <p className="muted-line">
            Проверка выполняется в вашем браузере тем же открытым кодом, что и консольная утилита <code>npm run verify</code>. Сервер в ней не участвует,
            кроме выдачи своего открытого ключа.
          </p>
        </div>
        {error && <p className="notice danger">{error}</p>}
        {pkg && (
          <dl className="verify-meta">
            <div>
              <dt>Розыгрыш</dt>
              <dd className="mono">{pkg.game.gameId}</dd>
            </div>
            <div>
              <dt>Состав</dt>
              <dd>{pkg.index.components.map((c) => c.symbol).join(' · ')}</dd>
            </div>
            <div>
              <dt>Бар P₀ (UTC)</dt>
              <dd className="mono">{pkg.index.p0MarketTime.replace('T', ' ').slice(0, 16)}</dd>
            </div>
            <div>
              <dt>Источник</dt>
              <dd>{pkg.game.sourceDescription}</dd>
            </div>
          </dl>
        )}
        {busy && <p className="muted-line">Пересчёт…</p>}
        {report && (
          <>
            <p className={`verify-summary ${report.ok ? 'ok' : 'fail'}`}>{report.summary}</p>
            <ol className="checks">
              {report.checks.map((c) => (
                <li key={c.id} className={c.status}>
                  <span className="check-icon" aria-label={c.status}>
                    {ICON[c.status]}
                  </span>
                  <div>
                    <b>{c.title}</b>
                    <ul>
                      {c.details.map((d) => (
                        <li key={d}>{d}</li>
                      ))}
                    </ul>
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
        {pkg && (
          <div className="verify-export">
            <span className="muted-line">Выгрузки для сравнения с внешними источниками котировок (UTC, разделитель «;»):</span>
            <div className="btn-row">
              <button className="btn tiny" onClick={() => saveFile(`${stem}-quotes.csv`, componentsCsv(pkg), 'text/csv')}>
                Котировки акций, CSV
              </button>
              <button className="btn tiny" onClick={() => saveFile(`${stem}-index.csv`, aggregateCsv(pkg), 'text/csv')}>
                Бары индекса, CSV
              </button>
              <button className="btn tiny" onClick={() => saveFile(`${stem}-actions.csv`, actionsCsv(pkg), 'text/csv')}>
                Журнал ходов, CSV
              </button>
              <button className="btn tiny" onClick={() => saveFile(`${stem}.json`, JSON.stringify(signed, null, 2), 'application/json')}>
                Пакет, JSON
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
